import React, { useRef, useImperativeHandle, type Ref } from 'react';
import { Box } from '@customerio/pluma-components/react';
import { useDragDrop, type DragDropData } from './use-drag-drop';
import * as styles from './index.css';

interface FileUploadAreaProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
  dragData?: DragDropData;
  children: React.ReactNode;
  style?: React.CSSProperties;
  acceptedMimeTypes?: string[];
  ref?: Ref<FileUploadAreaRef>;
}

export interface FileUploadAreaRef {
  triggerFileInput: () => void;
}

export function FileUploadArea({
  onFilesSelected,
  disabled = false,
  children,
  style,
  acceptedMimeTypes,
  ref,
}: FileUploadAreaProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { dragData, dragDropProps, fileInputProps } = useDragDrop(onFilesSelected, disabled, acceptedMimeTypes);

  const handleButtonClick = () => {
    if (disabled || fileInputRef.current == null) return;
    fileInputRef.current.click();
  };

  useImperativeHandle(ref, () => ({
    triggerFileInput: handleButtonClick,
  }));

  return (
    <Box
      className={styles.uploadArea}
      data-drag-over={dragData.isDragOver}
      data-disabled={disabled}
      style={style}
      {...dragDropProps}
    >
      {/* oxlint-disable-next-line pluma/enforce-pluma-elements -- native file input is required for browser file picker APIs. */}
      <input ref={fileInputRef} {...fileInputProps} />

      {children}

      <Box
        className={styles.dragOverlay}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex="overlay"
        style={{ top: 0, left: 0, right: 0, bottom: 0 }}
        data-visible={dragData.isDragOver}
      />
    </Box>
  );
}
